import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getDepositAmountPercentage, isDefaultDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOption, getCheapestAvailablePackageOption } from 'selectors/offerPage/offerPageSelectors'
import GeoContext from 'contexts/geoContext'
import ReserveForZeroPolicyModal from '../ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import ReserveForZeroAndDepositPolicyModal from '../ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import ModalContext from 'contexts/ModalContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import moment from 'moment'

const Container = styled.div`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  display: flex;
  padding: ${rem(16)};
`

const RightSide = styled.div`
  margin-inline-start: ${rem(8)};

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: baseline;
  }
`

const StyledLink = styled(TextLink)`
  ${mediaQueryUp.desktop} {
    margin-inline-start: ${rem(8)};
  }
`

interface Props {
  packageView: App.OfferPackageOptionView
  offer: App.Offer
  checkIn: string
  defaultDepositAmountPercentage: number
}

function ReserveForZeroPaymentBanner({ offer, checkIn, packageView, defaultDepositAmountPercentage }: Props) {
  const showModal = useContext(ModalContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { currentCurrency } = useContext(GeoContext)
  const { hotelTotal } = packageView

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelTotal)

  const isOfferUsingDefaultDepositPercentage = isDefaultDepositAmountPercentage(offer)

  const onFindOutMore = useCallback(async() => {
    if (depositsAvailable) {
      showModal(
        <ReserveForZeroAndDepositPolicyModal
          depositAmountPercentage={depositAmountPercentage}
          offerType={offer.type}
          depositThresholds={offer.depositThresholds}
          depositAmount={offer.depositAmount}
        />,
      )
    } else {
      showModal(
        <ReserveForZeroPolicyModal />,
      )
    }
  }, [depositsAvailable, showModal, depositAmountPercentage, offer])

  const depositBoldTag = (chunks: React.ReactNode) => <BodyText as="span" variant="medium" weight="bold">{chunks}</BodyText>
  const depositOptionsMessage = isOfferUsingDefaultDepositPercentage
    ? (
        <FormattedMessage
          id="A3qOj99JjkY9YmIVgmH9"
          defaultMessage="<bold>Flexible payment options:</bold> Reserve your booking for free or with a deposit"
          description="Banner text explaining reserve for free or deposit options without percentage"
          values={{ bold: depositBoldTag }}
        />
      )
    : (
        <FormattedMessage
          id="xPheeA2GWMhTsdNqh-RO"
          defaultMessage="<bold>Flexible payment options:</bold> Reserve your booking for free or with a {depositPercentage}% deposit"
          description="Banner text explaining reserve for free or deposit options with percentage"
          values={{ depositPercentage: depositAmountPercentage, bold: depositBoldTag }}
        />
      )

  return (
    <Container>
      <LineInfoCircleIcon colour="primary" />
      <RightSide>
        {depositsAvailable && (
          <BodyText variant="medium">
            {depositOptionsMessage}
          </BodyText>
        )}
        {!depositsAvailable && (
          <BodyText variant="medium">
            <FormattedMessage
              id="oMoW2liZCJgQKt-TwgSX"
              defaultMessage="<bold>Reserve for free:</bold> We’ll hold your reservation for 48 hours so you have time to think it over."
              description="Banner text explaining 48 hour reservation hold for free"
              values={{
                bold: (chunks) => <BodyText as="span" variant="medium" weight="bold">{chunks}</BodyText>,
              }}
            />
          </BodyText>
        )}
        <StyledLink onClick={onFindOutMore} size="medium">
          <FormattedMessage
            id="IXxr4-OcVyi_v5-TDuko"
            defaultMessage="Learn more"
            description="Hyperlink text to learn more about payment options"
          />
        </StyledLink>
      </RightSide>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  packageView: getCheapestSelectedPackageOption(appState, state) ?? getCheapestAvailablePackageOption(state, appState),
  offer: state.offer,
  checkIn: state.checkInDate,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}), ReserveForZeroPaymentBanner)
